import { createContext } from "react";

export type Theme = "dark" | "light" | "system";

/** Light mode variants */
export type LightVariant = "default" | "soft" | "cool";

/** Dark mode variants */
export type DarkVariant = "default" | "midnight" | "amoled" | "nord" | "dracula" | "catppuccin";

/** Available language codes for translation (top 20 world languages) */
export type LanguageCode =
  | 'en'  // English
  | 'zh'  // Chinese (Mandarin)
  | 'hi'  // Hindi
  | 'es'  // Spanish
  | 'fr'  // French
  | 'ar'  // Arabic
  | 'bn'  // Bengali
  | 'pt'  // Portuguese
  | 'ru'  // Russian
  | 'ja'  // Japanese
  | 'de'  // German
  | 'ko'  // Korean
  | 'vi'  // Vietnamese
  | 'it'  // Italian
  | 'tr'  // Turkish
  | 'pl'  // Polish
  | 'nl'  // Dutch
  | 'id'  // Indonesian
  | 'th'  // Thai
  | 'uk'; // Ukrainian

/** Language metadata for UI display */
export interface LanguageOption {
  code: LanguageCode;
  name: string;
  flag: string;
}

/** All available languages (top 20 world languages by total speakers) */
export const AVAILABLE_LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
];

export interface AppConfig {
  /** Current theme */
  theme: Theme;
  /** Light mode variant */
  lightVariant: LightVariant;
  /** Dark mode variant */
  darkVariant: DarkVariant;
  /** Custom primary color in HSL format (e.g., "271 81% 56%") */
  primaryColor?: string;
  /** @deprecated Use relayUrls instead. Kept for backward compatibility */
  relayUrl?: string;
  /** List of relay URLs */
  relayUrls: string[];
  /** Git repository URL to use as project template */
  projectTemplate: string;
  /** ESM CDN URL for package imports */
  esmUrl: string;
  /** HTTP proxy used to bypass CORS for AI and Git operations */
  corsProxy: string;
  /** Favicon URL template (e.g., "https://external-content.duckduckgo.com/ip3/{hostname}.ico") */
  faviconUrl: string;
  /** Preview domain for iframe sandboxing (e.g., "local-shakespeare.dev") */
  previewDomain: string;
  /** Selected language */
  language?: string;
  /** Whether to show the showcase section */
  showcaseEnabled: boolean;
  /** Npub of the showcase moderator */
  showcaseModerator: string;
  /** Nostr Git Servers (hostnames) */
  ngitServers: string[];
  /** Custom system prompt template for "UI + Superpowers" mode (Nostr projects) */
  systemPromptTemplate?: string;
  /** Custom system prompt template for "Just UI" mode (vanilla React projects) */
  systemPromptTemplateVanilla?: string;
  /** Custom AGENTS.md file template for new projects */
  agentsFileTemplate?: string;
  /** Whether to automatically translate new features to selected languages */
  autoTranslate?: boolean;
  /** Languages to auto-translate to (language codes) */
  autoTranslateLanguages?: LanguageCode[];
}

export interface AppContextType {
  /** Current application configuration */
  config: AppConfig;
  /** Update configuration using a callback that receives current config and returns new config */
  updateConfig: (updater: (currentConfig: Partial<AppConfig>) => Partial<AppConfig>) => void;
  /** Optional list of preset relays to display in the RelaySelector */
  presetRelays?: { name: string; url: string }[];
}

export const AppContext = createContext<AppContextType | undefined>(undefined);
